// Recent searches storage — a small, honest "recency" feature backed by the
// user's own past queries on this device (localStorage), not a server endpoint.
// Kept out of the component so AuthContext can clear it on sign-out without an
// import cycle (the component reads AuthContext).
// Story #6760: one key per signed-in person. The old single shared key
// ('if-recent-searches') showed the previous person's queries to the next
// person on the same browser. It is never read any more; sign-out removes it.
const KEY_PREFIX = 'if-recent-searches'
const MAX_RECENT = 6

const storageKey = (userId: string) => `${KEY_PREFIX}:${userId}`

/** Sign-out: drop every person's recent searches and the old shared key. */
export function clearRecentSearches() {
  try {
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && (k === KEY_PREFIX || k.startsWith(`${KEY_PREFIX}:`))) doomed.push(k)
    }
    doomed.forEach((k) => localStorage.removeItem(k))
  } catch {
    // localStorage unavailable — nothing was stored either.
  }
}

export function getRecentSearches(userId: string | undefined): string[] {
  if (!userId) return []
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((q): q is string => typeof q === 'string') : []
  } catch {
    return []
  }
}

export function addRecentSearch(query: string, userId: string | undefined) {
  const q = query.trim()
  if (!q || !userId) return
  try {
    const existing = getRecentSearches(userId).filter((e) => e.toLowerCase() !== q.toLowerCase())
    const next = [q, ...existing].slice(0, MAX_RECENT)
    localStorage.setItem(storageKey(userId), JSON.stringify(next))
  } catch {
    // localStorage unavailable (private browsing, quota) — recent searches
    // simply won't persist. Not fatal to search itself.
  }
}
